'use client';

import * as React from 'react';

import { baseUrl } from '@workspace/routes';

/** Demo pages a card can open (the dashboard demo's section names). */
export type DemoSection =
  | 'monitors'
  | 'accounts'
  | 'projects'
  | 'audience'
  | 'campaigns'
  | 'approvals'
  | 'person';

const OPEN_EVENT = 'lumnis-demo:open';

/** Scroll to the live demo and switch it to a page (used by the cards). */
export function openDemoSection(section: DemoSection): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: section }));
}

/**
 * The live product under the logos (Abu, Oct 28): the dashboard's public
 * /demo/monitor page, the production pages over saved, scrubbed snapshots.
 * Lazy so the homepage does not load the dashboard up front. Cards elsewhere
 * on the page switch it with openDemoSection; the frame says when it is
 * ready, and a request made before then waits for it.
 */
export function PipelineLiveDemo(): React.JSX.Element {
  const section = React.useRef<HTMLElement>(null);
  const frame = React.useRef<HTMLIFrameElement>(null);
  const ready = React.useRef(false);
  const pending = React.useRef<DemoSection | null>(null);
  // The frame swallows wheel and trackpad scrolling, so a layer covers it
  // until the visitor clicks in; leaving the demo puts the layer back.
  const [interactive, setInteractive] = React.useState(false);
  // The dashboard takes a moment to load; say so instead of a blank frame.
  const [loaded, setLoaded] = React.useState(false);
  // The frame gets its address only after this page is listening, so its
  // load event and its "ready" message can't arrive before hydration and be
  // lost (which left the loading note up and the cards unable to switch it).
  const [src, setSrc] = React.useState<string>();
  const dashboardOrigin = new URL(baseUrl.Dashboard).origin;

  React.useEffect(() => {
    const send = (target: DemoSection) =>
      frame.current?.contentWindow?.postMessage(
        { type: 'lumnis-demo:open', section: target },
        dashboardOrigin
      );
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== dashboardOrigin) return;
      if ((event.data as { type?: unknown } | null)?.type !== 'lumnis-demo:ready')
        return;
      ready.current = true;
      // The demo says "ready" once it renders, often well before the frame's
      // load event (which waits on every image), so either clears the note.
      setLoaded(true);
      if (pending.current) send(pending.current);
      pending.current = null;
    };
    const onOpen = (event: Event) => {
      const target = (event as CustomEvent<DemoSection>).detail;
      section.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (ready.current) send(target);
      else pending.current = target;
    };
    window.addEventListener('message', onMessage);
    window.addEventListener(OPEN_EVENT, onOpen);
    // Load the dashboard when the demo nears the screen. Not loading="lazy":
    // this layout scrolls the body, and the browser's lazy loading never
    // started the frame here (the blank demo, Oct 29).
    const load = () => setSrc(`${baseUrl.Dashboard}/demo/monitor`);
    const near =
      typeof IntersectionObserver === 'undefined'
        ? (load(), null)
        : new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) {
                load();
                near?.disconnect();
              }
            },
            { rootMargin: '800px 0px' }
          );
    if (near && section.current) near.observe(section.current);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener(OPEN_EVENT, onOpen);
      near?.disconnect();
    };
  }, [dashboardOrigin]);

  // Never leave the note over a demo that rendered without saying so.
  React.useEffect(() => {
    if (!src) return;
    const fallback = window.setTimeout(() => setLoaded(true), 10000);
    return () => window.clearTimeout(fallback);
  }, [src]);

  return (
    <section
      ref={section}
      className="relative scroll-mt-20 bg-[color:var(--paper)] py-12 md:py-16"
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        <div
          className="relative overflow-hidden rounded-[20px] border border-[color:var(--hairline)] p-2 shadow-sm md:p-3"
          style={{
            background: 'color-mix(in srgb, #00C896 6%, var(--paper))'
          }}
          onMouseLeave={() => setInteractive(false)}
        >
          {!interactive && (
            <button
              type="button"
              aria-label="Explore the live product"
              onClick={() => setInteractive(true)}
              className="absolute inset-0 z-10 cursor-pointer"
            />
          )}
          {!loaded && (
            <div className="absolute inset-2 z-[5] flex items-center justify-center rounded-[14px] bg-white text-[14px] text-[color:var(--ink-4)] md:inset-3">
              Loading the live product…
            </div>
          )}
          <iframe
            ref={frame}
            src={src}
            title="Account prioritization in Lumnis"
            onLoad={() => src && setLoaded(true)}
            allow="clipboard-write"
            className="block h-[min(78vh,760px)] min-h-[480px] w-full rounded-[14px] border border-[color:var(--hairline)] bg-white"
          />
        </div>
        {/* The hint sits under the frame, never over the product. Trust came
            back whenever buyers saw the source link (lost-deal research,
            Oct 29), so the frame says so. */}
        <p className="mt-3 text-center text-[14px] font-medium text-[color:var(--ink)]">
          Every signal links to its source.
        </p>
        <p
          aria-hidden
          className={`mt-1 text-center text-[13px] text-[color:var(--ink-4)] transition-opacity ${interactive ? 'opacity-0' : 'opacity-100'}`}
        >
          Click the product to explore it
        </p>
      </div>
    </section>
  );
}
